// ─── Performance Mode Hook ──────────────────────────────────────────────────
// Orchestrates device detection with 4 reactive runtime monitors:
//   1. Initial detection — deferred via requestIdleCallback (never blocks first paint)
//   2. Battery API listener — auto-downgrades on low battery
//   3. Network change listener — adapts to connection quality changes
//   4. Live FPS monitor — auto-kills Vanta if rendering is too slow
//
// Results are cached in sessionStorage so benchmarks only run ONCE per session.
// On detection failure, defaults to LOW-SPEC (safe) to prevent crashes.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from 'react';
import { detectDeviceSpecs, DeviceSpecs, PerformanceTier } from '../utils/deviceDetector';

const STORAGE_KEY = 'pmz_device_performance_mode';

// ── Type declaration for Battery API (Android/Chrome only) ──────────────────
interface BatteryManager extends EventTarget {
    readonly level: number;
    readonly charging: boolean;
}

export function usePerformanceMode() {
    // Start with isLoading=true so Vanta does NOT render until detection completes.
    // This prevents hydration mismatch and UI flicker on low-end devices.
    const [performanceTier, setPerformanceTier] = useState<PerformanceTier>('high');
    const [isLowSpec, setIsLowSpec] = useState<boolean>(false);
    const [specs, setSpecs] = useState<DeviceSpecs | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const fpsMonitorRef = useRef<number | null>(null);

    // ── Helper: update all state from a tier ─────────────────────────────────
    const applyTier = useCallback((tier: PerformanceTier, detectedSpecs: DeviceSpecs) => {
        setPerformanceTier(tier);
        setIsLowSpec(tier === 'low');
        setSpecs(detectedSpecs);
        setIsLoading(false);
    }, []);

    // ── Helper: persist tier change to sessionStorage ────────────────────────
    const persistTier = useCallback((tier: PerformanceTier) => {
        try {
            const cached = sessionStorage.getItem(STORAGE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                parsed.performanceTier = tier;
                parsed.isLowSpec = tier === 'low';
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
            }
        } catch { /* ignore storage errors */ }
    }, []);

    // ── 1. Initial Detection (deferred, cached) ─────────────────────────────
    // Defer detection by 1.5 seconds so that the page load, initial paint,
    // and layout stabilization (LCP) can complete without any resource competition.
    // Once deferred, uses requestIdleCallback (or setTimeout fallback) to run during idle.
    useEffect(() => {
        if (typeof window === 'undefined') return;

        let idleId: number | null = null;
        let timeoutId: number | null = null;

        const runDetection = () => {
            try {
                // Check cache first — avoids re-running GPU/benchmark
                const cached = sessionStorage.getItem(STORAGE_KEY);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    const tier = parsed.performanceTier || (parsed.isLowSpec ? 'low' : 'high');
                    applyTier(tier, parsed.specs);
                    return;
                }

                // Run full detection
                const detected = detectDeviceSpecs();
                applyTier(detected.performanceTier, detected);

                // Log results for debugging (only on first run, removed in production by Next.js compiler)
                console.log('⚡ Performance Profile:', {
                    OS: detected.os,
                    Type: detected.deviceType,
                    Cores: detected.cpuCores,
                    RAM: `${detected.ramGB}GB`,
                    GPU: detected.gpu,
                    Vendor: detected.gpuVendor,
                    Benchmark: `${detected.benchmarkTimeMs.toFixed(2)}ms`,
                    Tier: detected.performanceTier,
                    ReducedMotion: detected.prefersReducedMotion,
                    Network: detected.networkEffectiveType,
                    SaveData: detected.saveData,
                    PixelRatio: detected.screenPixelRatio,
                });

                // Cache results for the session
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
                    performanceTier: detected.performanceTier,
                    isLowSpec: detected.isLowSpec,
                    specs: detected,
                }));
            } catch (e) {
                console.error('Performance detection failed:', e);
                // Safer default: assume low-spec on failure to prevent crashes
                setPerformanceTier('low');
                setIsLowSpec(true);
                setIsLoading(false);
            }
        };

        // Delay starting the detection process by 1.5 seconds
        timeoutId = window.setTimeout(() => {
            if ('requestIdleCallback' in window) {
                idleId = requestIdleCallback(runDetection, { timeout: 2000 });
            } else {
                runDetection();
            }
        }, 1500);

        return () => {
            if (timeoutId !== null) window.clearTimeout(timeoutId);
            if (idleId !== null && 'cancelIdleCallback' in window) cancelIdleCallback(idleId);
        };
    }, [applyTier]);

    // ── 2. Battery API (async — Android/Chrome only) ─────────────────────────
    // If battery drops below 15% while not charging, downgrade to low tier
    // to conserve power and prevent thermal throttling.
    useEffect(() => {
        if (typeof window === 'undefined' || !('getBattery' in navigator)) return;

        let mounted = true;
        let batteryRef: BatteryManager | null = null;
        let levelHandler: (() => void) | null = null;
        let chargingHandler: (() => void) | null = null;

        const getBattery = (navigator as Navigator & { getBattery: () => Promise<BatteryManager> }).getBattery;

        getBattery.call(navigator).then((battery: BatteryManager) => {
            if (!mounted) return;
            batteryRef = battery;

            const checkBattery = () => {
                if (!mounted) return;
                if (battery.level < 0.15 && !battery.charging && performanceTier !== 'low') {
                    console.log(`🔋 Low battery (${Math.round(battery.level * 100)}%) — switching to low-spec mode`);
                    setPerformanceTier('low');
                    setIsLowSpec(true);
                    persistTier('low');
                }
            };

            checkBattery();
            levelHandler = checkBattery;
            chargingHandler = checkBattery;
            battery.addEventListener('levelchange', levelHandler);
            battery.addEventListener('chargingchange', chargingHandler);
        }).catch(() => { /* Battery API not supported or blocked */ });

        return () => {
            mounted = false;
            if (batteryRef && levelHandler) batteryRef.removeEventListener('levelchange', levelHandler);
            if (batteryRef && chargingHandler) batteryRef.removeEventListener('chargingchange', chargingHandler);
        };
    }, [performanceTier, persistTier]);

    // ── 3. Network Change Listener ───────────────────────────────────────────
    // If connection degrades (e.g., user enters a tunnel), downgrade dynamically.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const connection = (navigator as Navigator & { connection?: EventTarget & { effectiveType: string; saveData: boolean } }).connection;
        if (!connection) return;

        const handleNetworkChange = () => {
            const effectiveType = connection.effectiveType;
            const isSaveData = connection.saveData;

            if ((isSaveData || ['slow-2g', '2g'].includes(effectiveType)) && performanceTier === 'high') {
                console.log(`📶 Slow network detected (${effectiveType}) — downgrading to medium tier`);
                setPerformanceTier('medium');
                persistTier('medium');
            }
        };

        connection.addEventListener('change', handleNetworkChange);
        return () => connection.removeEventListener('change', handleNetworkChange);
    }, [performanceTier, persistTier]);

    // ── 4. Runtime FPS Monitoring (Dynamic Downgrade) ────────────────────────
    // After Vanta starts, we measure real rendering FPS. If frames consistently
    // drop below 20 FPS for 6+ seconds, we auto-kill Vanta to stop jank.
    // Monitoring only runs when tier is 'high' or 'medium' (not already 'low').
    useEffect(() => {
        if (typeof window === 'undefined' || isLoading || performanceTier === 'low') return;

        let frames = 0;
        let lastTime = performance.now();
        let consecutiveLowReadings = 0;

        const FPS_THRESHOLD = 20;           // Below this = struggling
        const CHECK_INTERVAL_MS = 2000;     // Measurement window
        const DOWNGRADE_AFTER = 3;          // 3 × 2s = 6 seconds of sustained lag

        const measureFps = () => {
            frames++;
            const now = performance.now();
            const elapsed = now - lastTime;

            if (elapsed >= CHECK_INTERVAL_MS) {
                const fps = Math.round((frames * 1000) / elapsed);
                frames = 0;
                lastTime = now;

                if (fps < FPS_THRESHOLD) {
                    consecutiveLowReadings++;
                    if (consecutiveLowReadings >= DOWNGRADE_AFTER) {
                        console.log(`🐌 Sustained low FPS (${fps}) for ${DOWNGRADE_AFTER * 2}s — auto-disabling Vanta`);
                        setPerformanceTier('low');
                        setIsLowSpec(true);
                        persistTier('low');
                        return; // Stop monitoring loop
                    }
                } else {
                    consecutiveLowReadings = 0; // Reset on recovery
                }
            }

            fpsMonitorRef.current = requestAnimationFrame(measureFps);
        };

        // Delay 3s to let Vanta/Three.js fully initialize before measuring
        const startDelay = window.setTimeout(() => {
            fpsMonitorRef.current = requestAnimationFrame(measureFps);
        }, 3000);

        return () => {
            clearTimeout(startDelay);
            if (fpsMonitorRef.current !== null) {
                cancelAnimationFrame(fpsMonitorRef.current);
                fpsMonitorRef.current = null;
            }
        };
    }, [isLoading, performanceTier, persistTier]);

    return { isLowSpec, performanceTier, specs, isLoading };
}
